import { useEffect, useRef, useState } from 'react';

/**
 * Publishes one element's height on an ancestor as a CSS custom property, so
 * anything that has to park below it (a second sticky element, say) can read
 * the real height instead of repeating a magic number that drifts the moment
 * the first element's content changes.
 *
 * Returns refs for the ancestor that carries the variable and for the element
 * being measured; when nothing is measured the variable is set to `0px`.
 */
export function useStickyOffsetVar(varName) {
    const [host, setHost] = useState(null);
    const [measured, setMeasured] = useState(null);
    // Keeps the variable's last value out of the effect's dependencies.
    const written = useRef(null);

    useEffect(() => {
        if (!host) return undefined;
        const write = (value) => {
            if (written.current === value) return;
            written.current = value;
            host.style.setProperty(varName, value);
        };
        if (!measured) {
            write('0px');
            return undefined;
        }
        const measure = () => write(`${Math.round(measured.getBoundingClientRect().height)}px`);
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(measured);
        return () => observer.disconnect();
    }, [host, measured, varName]);

    return { hostRef: setHost, measuredRef: setMeasured };
}
